/**
 * Browser stand-in for a later on-device photo store. Each file stays tied to
 * the checklist item it was captured for. A mobile app can persist this same
 * list and run the upload queue unchanged.
 */
export type QcPhotoKind = "fail" | "pass";
export type QcPhotoStatus = "queued" | "sending" | "failed";

export type QcQueuedPhoto = {
  id: string;
  sessionId: string;
  itemId: string;
  kind: QcPhotoKind;
  file: File;
  status: QcPhotoStatus;
  error: string | null;
};

export const QC_FAIL_PHOTO_CAP = 3;

export function acceptQcPhoto(
  queue: readonly QcQueuedPhoto[],
  input: {
    id: string;
    sessionId: string;
    itemId: string;
    kind: QcPhotoKind;
    file: File;
    savedFailCount: number;
  },
): { queue: QcQueuedPhoto[]; accepted: boolean } {
  const mine = queue.filter((row) => row.itemId === input.itemId && row.kind === input.kind);
  if (input.kind === "fail" && input.savedFailCount + mine.length >= QC_FAIL_PHOTO_CAP) {
    return { queue: [...queue], accepted: false };
  }
  if (input.kind === "pass" && mine.length >= 2) {
    return { queue: [...queue], accepted: false };
  }
  return {
    accepted: true,
    queue: [
      ...queue,
      {
        id: input.id,
        sessionId: input.sessionId,
        itemId: input.itemId,
        kind: input.kind,
        file: input.file,
        status: "queued",
        error: null,
      },
    ],
  };
}

export function removeQcPhoto(queue: readonly QcQueuedPhoto[], id: string): QcQueuedPhoto[] {
  const target = queue.find((row) => row.id === id);
  if (!target || target.status === "sending") return [...queue];
  return queue.filter((row) => row.id !== id);
}

export function retryQcPhoto(queue: readonly QcQueuedPhoto[], id: string): QcQueuedPhoto[] {
  return queue.map((row) => (row.id === id && row.status === "failed" ? { ...row, status: "queued", error: null } : row));
}
